import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { loginAdmin } from '../utils/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { Eye, EyeOff, Lock, Mail, AlertCircle, ShieldCheck, Activity, ArrowRight, Server } from 'lucide-react';
import logo from '../assets/dozy-logo.svg';
import '../styles/Login.css';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePassword = (password) => password.length >= 8;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Por favor, insira um email válido');
      return;
    }

    if (!validatePassword(password)) {
      setError('A senha deve ter pelo menos 8 caracteres');
      return;
    }

    setLoading(true);

    try {
      const response = await loginAdmin(email, password);
      localStorage.setItem('authToken', response.token);

      if (!response || !response.token) {
        throw new Error('Token não recebido do servidor');
      }

      const userData = {
        id: response.id,
        email: response.email,
        role: response.role || 'admin',
        token: response.token,
        full_name: response.full_name || 'Administrador',
        avatar: response.avatar,
      };

      login(userData);
      navigate('/admin');
    } catch (err) {
      console.error('Login error:', err);
      if (err.response?.status === 401) {
        setError('Email ou senha incorretos');
      } else if (err.response?.status === 403) {
        setError('Acesso negado. Apenas administradores.');
      } else if (err.request) {
        setError('Erro de conexão. Verifique se o servidor está online.');
      } else {
        setError(err.message || 'Erro durante o login');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <aside className="admin-login-visual">
        <div className="admin-login-brand"><img src={logo} alt="Dozy" /><span>Dozy <small>Admin</small></span></div>
        <div className="admin-login-message"><span>Centro de operações</span><h1>Decisões seguras.<br/>Operação sob controlo.</h1><p>Acompanhe utilizadores, pagamentos e qualidade da plataforma num único ambiente protegido.</p></div>
        <div className="admin-login-signals"><div><Activity/><span><strong>Monitorização ativa</strong><small>Indicadores em tempo real</small></span></div><div><Server/><span><strong>Ambiente reservado</strong><small>Acesso exclusivo da equipa autorizada</small></span></div></div>
      </aside>

      <main className="admin-login-panel">
        <div className="admin-login-form-wrap">
          <div className="admin-login-mobile-brand"><img src={logo} alt="Dozy" /><span>Dozy Admin</span></div>
          <div className="admin-login-heading"><div><ShieldCheck /></div><span>Acesso protegido</span><h2>Entrar no painel</h2><p>Utilize as credenciais administrativas atribuídas à sua conta.</p></div>

          {error && (
            <div className="admin-login-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="admin-login-form">
            <div className="admin-login-field">
              <label>Email administrativo</label>
              <div>
                <Mail />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@dozy.ao"
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            <div className="admin-login-field">
              <label>Palavra-passe</label>
              <div>
                <Lock />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="admin-password-toggle"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="admin-login-options">
              <label>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Lembrar este dispositivo</span>
              </label>
              <span><Lock /> Sessão protegida</span>
            </div>

            {/* Sem esta ligação a página de recuperação existia e ninguém lá
                chegava — foi o mesmo defeito das empresas por confirmar. */}
            <p style={{ textAlign: 'right', margin: '-4px 0 4px' }}>
              <Link to="/recuperar" style={{ fontSize: '.82rem', color: '#60a5fa', textDecoration: 'none' }}>
                Esqueceu a palavra-passe?
              </Link>
            </p>

            <button
              type="submit"
              disabled={loading}
              className="admin-login-submit"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>A autenticar...</span>
                </>
              ) : (
                <>Entrar no painel <ArrowRight /></>
              )}
            </button>
          </form>
          <p className="admin-login-footer">Dozy Admin · Ambiente monitorizado · {new Date().getFullYear()}</p>
        </div>
      </main>
    </div>
  );
};

export default AdminLogin;
